import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { hideSizeRequiredMessage } from '@theme/product-form';

/** Session-wide EU/UK/US tab — colourways are separate Shopify products sharing one PDP flow. */
const FOOTWEAR_SIZE_REGION_SESSION_KEY = 'shopper-footwear-size-region';

/** @type {string | null} */
let shopperFootwearSizeRegion = null;

/**
 * @returns {string | null}
 */
function loadShopperFootwearSizeRegion() {
  if (shopperFootwearSizeRegion) return shopperFootwearSizeRegion;

  if (typeof sessionStorage !== 'undefined') {
    shopperFootwearSizeRegion = sessionStorage.getItem(FOOTWEAR_SIZE_REGION_SESSION_KEY);
  }

  return shopperFootwearSizeRegion;
}

/**
 * @param {string | undefined} _productId
 * @param {string} region
 */
export function rememberFootwearSizeRegion(_productId, region) {
  if (!region) return;

  shopperFootwearSizeRegion = region;

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(FOOTWEAR_SIZE_REGION_SESSION_KEY, region);
  }
}

/**
 * @param {string | undefined} [_productId]
 * @returns {string | null}
 */
export function recallFootwearSizeRegion(_productId) {
  return loadShopperFootwearSizeRegion();
}

/**
 * Re-applies the shopper's region tab to pickers inside a section after innerHTML swap.
 * @param {Element} section
 */
export function applyFootwearSizeRegionToSection(section) {
  const region = recallFootwearSizeRegion();
  if (!region) return;

  const apply = () => {
    for (const picker of section.querySelectorAll('variant-picker')) {
      if (!(picker instanceof HTMLElement)) continue;
      picker.dataset.selectedSizeRegion = region;
      if (picker instanceof VariantPicker) {
        picker.applyShopperSizeRegion();
      }
    }
  };

  queueMicrotask(() => {
    apply();
    requestAnimationFrame(apply);
  });
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** Ignores stale section fetches when the shopper clicks sizes quickly. */
  #fetchGeneration = 0;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  #regionChangeEvent = new CustomEvent('variant:region-change', {
    bubbles: true,
    cancelable: false,
  });

  /** @type {string | null} */
  #selectedSizeRegion = null;

  #boundHandleRegionSwitch = this.#handleRegionSwitch.bind(this);

  /** @type {(event: Event) => void} */
  #boundVariantChanged = this.variantChanged.bind(this);

  connectedCallback() {
    super.connectedCallback();

    this.#radios = [];
    this.#checkedIndices = [];

    // Bare PDP (no ?variant=): strip Shopify's default first-available size. A
    // gear-set switch counts as bare too: soft navigation swaps the new product in
    // before the URL changes, so the departing product's ?variant= is still there.
    // gear-set-size-carry.js then picks the carried sizes.
    if (
      !new URL(window.location.href).searchParams.has('variant') ||
      document.documentElement.hasAttribute('data-gear-set-switch')
    ) {
      this.#clearPreselectedSizes();
    }

    this.#refreshRadioCaches();

    for (const fieldset of /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || [])) {
      this.#updateLegendValue(fieldset);
    }

    this.removeEventListener('change', this.#boundVariantChanged);
    this.addEventListener('change', this.#boundVariantChanged);
    this.addEventListener('click', this.#boundHandleRegionSwitch);
    this.#resizeObserver.observe(this);

    if (this.#getShopperSizeRegion()) {
      this.#restoreSizeRegion();
      requestAnimationFrame(() => this.#restoreSizeRegion());
    }
  }

  /** Unchecks every size radio — sizes must be chosen explicitly by the shopper. */
  #clearPreselectedSizes() {
    for (const fieldset of this.querySelectorAll('fieldset.variant-option--grid')) {
      if (!(fieldset instanceof HTMLFieldSetElement)) continue;

      const radios = fieldset.querySelectorAll('input[type="radio"]');
      // Single-value ("One Size") options are auto-selected — there's nothing
      // for the shopper to choose — so keep them checked. Only strip Shopify's
      // default from real multi-size pickers.
      if (radios.length <= 1) continue;

      for (const radio of radios) {
        if (!(radio instanceof HTMLInputElement)) continue;

        radio.checked = false;
        radio.dataset.currentChecked = 'false';
        radio.dataset.previousChecked = 'false';
      }

      this.#updateLegendValue(fieldset);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#boundVariantChanged);
    this.removeEventListener('click', this.#boundHandleRegionSwitch);
    this.#resizeObserver.disconnect();
  }

  /** Rebuilds pill-animation caches after morph replaces radio nodes. */
  #refreshRadioCaches() {
    this.#radios = [];
    this.#checkedIndices = [];

    for (const fieldset of /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || [])) {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.checked || radio.dataset.currentChecked === 'true');
      this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
    }
  }

  /**
   * Handles region switcher clicks for size selector.
   * @param {MouseEvent} event
   */
  #handleRegionSwitch(event) {
    if (!(event.target instanceof Element)) return;

    const regionButton = event.target.closest('[data-action="switch-region"]');
    if (!regionButton || !(regionButton instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const newRegion = regionButton.dataset.region;
    if (!newRegion) return;

    const fieldset = regionButton.closest('fieldset.variant-option--grid[data-current-region]');
    if (!(fieldset instanceof HTMLFieldSetElement)) return;

    this.#selectedSizeRegion = newRegion;
    this.dataset.selectedSizeRegion = newRegion;
    rememberFootwearSizeRegion(this.dataset.productId, newRegion);
    this.#updateRegionDisplay(fieldset, newRegion);
  }

  /** Shopper's explicit tab choice — session-wide, not per variant or colourway product. */
  #getShopperSizeRegion() {
    return recallFootwearSizeRegion();
  }

  /** Public hook for section swaps and theme editor reloads. */
  applyShopperSizeRegion() {
    this.#restoreSizeRegion();
  }

  /**
   * Switches the footwear size region tab without a synthetic click (sticky ↔ PDP sync).
   * @param {string} region
   */
  switchSizeRegion(region) {
    if (!region) return;

    const fieldset = this.querySelector('fieldset.variant-option--grid[data-current-region]');
    if (!(fieldset instanceof HTMLFieldSetElement)) return;
    if (fieldset.dataset.currentRegion === region) return;

    this.#selectedSizeRegion = region;
    this.dataset.selectedSizeRegion = region;
    rememberFootwearSizeRegion(this.dataset.productId, region);
    this.#updateRegionDisplay(fieldset, region);
  }

  /** Keeps the shopper's tab across morph — only an explicit tab click is persisted. */
  #preserveSizeRegionBeforeMorph() {
    const saved = this.#getShopperSizeRegion();
    if (!saved) return;

    this.#selectedSizeRegion = saved;
    this.dataset.selectedSizeRegion = saved;
  }

  /** @type {Set<string>} */
  #sizebayRevealedFieldsetKeys = new Set();

  /** @type {{ look: HTMLElement, fieldsetKey: string | null }[]} */
  #preservedSizebayLooks = [];

  /** Preserves Sizebay ready state so morph does not re-enter the pending sync UI. */
  #preserveSizebaySyncBeforeMorph() {
    this.#sizebayRevealedFieldsetKeys.clear();

    for (const fieldset of this.querySelectorAll('fieldset.variant-option--grid[data-sizebay-ready]')) {
      if (!(fieldset instanceof HTMLFieldSetElement)) continue;

      const key = `${fieldset.dataset.fieldsetIndex ?? ''}:${fieldset.dataset.currentRegion ?? ''}`;
      if (key !== ':') {
        this.#sizebayRevealedFieldsetKeys.add(key);
      }
    }
  }

  /** @returns {boolean} */
  #areSizeSelectionsComplete() {
    const fieldsets = this.querySelectorAll('fieldset.variant-option--grid');
    if (fieldsets.length === 0) return true;

    for (const fieldset of fieldsets) {
      if (!(fieldset instanceof HTMLFieldSetElement)) continue;
      if (!fieldset.querySelector('input[type="radio"]:checked')) return false;
    }

    return true;
  }

  /** Re-applies Sizebay ready state after variant-picker morph. */
  #restoreSizebaySyncAfterMorph() {
    if (this.#sizebayRevealedFieldsetKeys.size === 0) return;

    for (const fieldset of this.querySelectorAll('fieldset.variant-option--grid')) {
      if (!(fieldset instanceof HTMLFieldSetElement)) continue;

      const key = `${fieldset.dataset.fieldsetIndex ?? ''}:${fieldset.dataset.currentRegion ?? ''}`;
      if (!this.#sizebayRevealedFieldsetKeys.has(key)) continue;

      fieldset.setAttribute('data-sizebay-ready', '');
      fieldset.removeAttribute('data-sizebay-awaiting-selection');
      this.#updateLegendValue(fieldset);
    }
  }

  /**
   * Detaches Sizebay's injected `#szb-look-N` mounts before the picker morph.
   *
   * Each mount holds the hidden `#szb-chart-button` that Sizebay's adopted "Find
   * my size" trigger clicks. The server HTML has no look, so the morph would
   * discard it, and the Sizebay prescript only rebuilds looks when the product
   * handle changes (a colourway switch) — never for a same-product size change.
   * Detach here and re-attach in `#reattachSizebayLooksAfterMorph` so the button
   * survives.
   */
  #detachSizebayLooksBeforeMorph() {
    this.#preservedSizebayLooks = [];

    for (const look of this.querySelectorAll('[id^="szb-look-"]')) {
      if (!(look instanceof HTMLElement)) continue;

      const fieldset = look.nextElementSibling;
      const fieldsetKey = fieldset instanceof HTMLElement ? (fieldset.dataset.key ?? null) : null;
      this.#preservedSizebayLooks.push({ look, fieldsetKey });
      look.remove();
    }
  }

  /** Re-attaches the Sizebay mounts detached in `#detachSizebayLooksBeforeMorph`. */
  #reattachSizebayLooksAfterMorph() {
    for (const { look, fieldsetKey } of this.#preservedSizebayLooks) {
      const fieldset = fieldsetKey ? this.querySelector(`fieldset[data-key="${CSS.escape(fieldsetKey)}"]`) : null;
      if (!(fieldset instanceof HTMLElement)) continue;

      const existing = fieldset.previousElementSibling;
      if (existing instanceof HTMLElement && existing.id.startsWith('szb-look-')) {
        existing.replaceWith(look);
      } else {
        fieldset.insertAdjacentElement('beforebegin', look);
      }
    }

    this.#preservedSizebayLooks = [];
  }

  /**
   * Freezes each `.size-guide-container` across the morph so Sizebay's adopted
   * <span> trigger isn't reverted to the server-rendered <size-guide> fallback.
   * The size chart URL and `szb-product-path` are product-level, so there is
   * nothing variant-specific to re-render here. Morph honors `data-skip-*` only
   * when both the old and new nodes carry the flags, so flag both trees; they
   * are stripped again in `#unfreezeSizebayGuideAfterMorph`. Combined-listing
   * switches go through `updateMain`, not this method, so a new colourway still
   * re-renders its container normally.
   * @param {Element} newSource
   * @returns {HTMLElement[]} The live containers that were frozen.
   */
  #freezeSizebayGuideBeforeMorph(newSource) {
    /** @type {HTMLElement[]} */
    const frozen = [];

    /** @param {Element} el */
    const freeze = (el) => {
      if (!(el instanceof HTMLElement)) return;
      el.setAttribute('data-skip-node-update', '');
      el.setAttribute('data-skip-subtree-update', '');
    };

    for (const el of this.querySelectorAll('.size-guide-container')) {
      freeze(el);
      if (el instanceof HTMLElement) frozen.push(el);
    }
    for (const el of newSource.querySelectorAll('.size-guide-container')) {
      freeze(el);
    }

    return frozen;
  }

  /** @param {HTMLElement[]} frozen */
  #unfreezeSizebayGuideAfterMorph(frozen) {
    for (const el of frozen) {
      el.removeAttribute('data-skip-node-update');
      el.removeAttribute('data-skip-subtree-update');
    }
  }

  /** Re-applies the shopper's region tab after variant-picker morph. */
  #restoreSizeRegion() {
    const saved = this.#getShopperSizeRegion();
    if (!saved) return;

    this.#selectedSizeRegion = saved;
    this.dataset.selectedSizeRegion = saved;

    for (const fieldset of this.querySelectorAll('fieldset.variant-option--grid[data-current-region]')) {
      if (!(fieldset instanceof HTMLFieldSetElement)) continue;
      this.#updateRegionDisplay(fieldset, saved);
    }
  }

  /**
   * @param {HTMLFieldSetElement} fieldset
   * @param {string} region
   */
  #updateRegionDisplay(fieldset, region) {
    const normalizedRegion = region.toLowerCase();
    fieldset.dataset.currentRegion = normalizedRegion;

    const labels = fieldset.querySelectorAll('[data-region-label]');
    const buttons = fieldset.querySelectorAll('[data-action="switch-region"]');

    labels.forEach((label) => {
      if (label instanceof HTMLElement) {
        const labelRegion = label.dataset.regionLabel?.toLowerCase() ?? '';
        label.hidden = labelRegion !== normalizedRegion;
      }
    });

    buttons.forEach((button) => {
      if (button instanceof HTMLElement) {
        const isActive = button.dataset.region?.toLowerCase() === normalizedRegion;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      }
    });

    this.#updateLegendValue(fieldset);
    this.dispatchEvent(this.#regionChangeEvent);
  }

  /**
   * Syncs the legend value with the checked size for the active region.
   * @param {HTMLFieldSetElement | null | undefined} fieldset
   */
  #updateLegendValue(fieldset) {
    if (!(fieldset instanceof HTMLFieldSetElement)) return;

    const legendValue = fieldset.querySelector('[data-legend-value]');
    if (!(legendValue instanceof HTMLElement)) return;

    const checked = fieldset.querySelector('input:checked');
    if (!(checked instanceof HTMLInputElement)) {
      legendValue.textContent = '';
      return;
    }

    const label = checked.closest('label');
    const region = fieldset.dataset.currentRegion;
    let text = checked.value;

    if (label instanceof HTMLElement) {
      const regionSpan =
        region != null && region !== '' ? label.querySelector(`[data-region-label="${region}"]`) : null;
      const visibleSpan = label.querySelector('[data-region-label]:not([hidden])');
      const span = regionSpan ?? visibleSpan;
      text = span?.textContent?.trim() ?? checked.value;
    }

    legendValue.textContent = text;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    const fieldset = event.target.closest('fieldset');
    if (fieldset instanceof HTMLFieldSetElement) {
      this.#updateLegendValue(fieldset);
    }

    if (fieldset instanceof HTMLElement && fieldset.matches('fieldset.variant-option--grid')) {
      hideSizeRequiredMessage(this);
    }

    // Capture selection in this turn — morph/Liquid must not be the source of truth.
    const optionValueIds = this.selectedOptionsValues;
    const sizesComplete = this.#areSizeSelectionsComplete();
    const selectedOptionValueId = selectedOption.dataset.optionValueId ?? '';

    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOptionValueId }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector, {
      optionValueIds,
      sizesComplete,
      selectedOptionValueId,
    });

    // URL: Horizon writes radio data-variant-id. Gear sets only persist when
    // every size picker is checked — otherwise a partial id pre-selects defaults.
    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;
    const sizeFieldsetCount = this.querySelectorAll('fieldset.variant-option--grid').length;

    if (isOnProductPage) {
      if (sizeFieldsetCount > 1 && !sizesComplete) {
        // Gear set incomplete — never persist a partial radio variant id.
        url.searchParams.delete('variant');
      } else if (sizeFieldsetCount > 1) {
        // Both sizes chosen — wait for fetch sync (radio id is still partial).
      } else if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }

      // Sync the live fieldset (cache can be stale after morph). Morph's
      // onBeforeUpdate preserves data-current-checked — keep it truthful.
      const liveFieldset = target.closest('fieldset');
      if (liveFieldset) {
        for (const radio of liveFieldset.querySelectorAll('input[type="radio"]')) {
          if (!(radio instanceof HTMLInputElement)) continue;
          const isCurrent = radio === target;
          radio.checked = isCurrent;
          radio.dataset.currentChecked = isCurrent ? 'true' : 'false';
          if (isCurrent) radio.dataset.previousChecked = 'false';
          radio.closest('label')?.classList.toggle('is-selected', isCurrent);
        }
      } else {
        target.checked = true;
      }
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueIds?: string[], sizesComplete?: boolean, selectedOptionValueId?: string }} [selection] - Selection captured at click time.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, selection = {}) {
    const fetchGeneration = ++this.#fetchGeneration;
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        // A newer click already started — never morph/dispatch stale HTML.
        if (fetchGeneration !== this.#fetchGeneration) return;

        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // Re-apply the click-time picks — Liquid/morph can drop checked state.
          this.#applyOptionValueSelection(selection.optionValueIds);

          const sizesComplete = selection.sizesComplete ?? this.#areSizeSelectionsComplete();
          const variantData = JSON.parse(textContent);
          const resource = sizesComplete ? variantData : null;

          this.#syncProductPageVariantParam(resource?.id ?? null, sizesComplete);

          const sourceId = selection.selectedOptionValueId || this.selectedOptionId;
          if (sourceId) {
            this.dispatchEvent(
              new VariantUpdateEvent(resource, sourceId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              }),
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Ensures radios match the option_values we requested (post-morph).
   * @param {string[] | undefined} optionValueIds
   */
  #applyOptionValueSelection(optionValueIds) {
    if (!Array.isArray(optionValueIds) || optionValueIds.length === 0) return;

    const selected = new Set(optionValueIds.map(String));

    for (const fieldset of this.querySelectorAll('fieldset')) {
      if (!(fieldset instanceof HTMLFieldSetElement)) continue;

      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      if (radios.length === 0) continue;

      const match = radios.find((radio) => selected.has(String(radio.dataset.optionValueId ?? '')));

      if (!match) {
        // Gear set partial pick: the shopper hasn't chosen this size component,
        // yet the server pre-selected its default (smallest). Clear it so the
        // component stays blank — the size gate keeps blocking add-to-cart and
        // the per-component notice can flag it. Single-value ("One Size") grids
        // (one radio) are auto-selected and must stay checked, so leave those.
        if (fieldset.matches('fieldset.variant-option--grid') && radios.length > 1) {
          for (const radio of radios) {
            if (!(radio instanceof HTMLInputElement)) continue;
            radio.checked = false;
            radio.dataset.currentChecked = 'false';
            radio.dataset.previousChecked = 'false';
            radio.closest('label')?.classList.remove('is-selected');
          }
          this.#updateLegendValue(fieldset);
        }
        continue;
      }

      for (const radio of radios) {
        if (!(radio instanceof HTMLInputElement)) continue;
        const isChecked = radio === match;
        radio.checked = isChecked;
        radio.dataset.currentChecked = isChecked ? 'true' : 'false';
        radio.dataset.previousChecked = 'false';
        radio.closest('label')?.classList.toggle('is-selected', isChecked);
      }

      if (fieldset.matches('fieldset.variant-option--grid')) {
        this.#updateLegendValue(fieldset);
      }
    }

    this.#refreshRadioCaches();
    this.updateVariantPickerCss();
  }

  /**
   * Persists ?variant= after option_values resolve (needed for gear sets).
   * @param {string | number | null | undefined} variantId
   * @param {boolean} sizesComplete
   */
  #syncProductPageVariantParam(variantId, sizesComplete) {
    if (this.dataset.templateProductMatch !== 'true') return;
    if (this.closest('product-card') || this.closest('quick-add-dialog')) return;

    const sizeFieldsetCount = this.querySelectorAll('fieldset.variant-option--grid').length;
    const url = new URL(window.location.href);

    if (sizeFieldsetCount > 1 && !sizesComplete) {
      url.searchParams.delete('variant');
    } else if (variantId != null && variantId !== '') {
      url.searchParams.set('variant', String(variantId));
    } else if (sizeFieldsetCount <= 1) {
      // Single-size / no size: optimistic URL from variantChanged is enough.
      return;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    this.#preserveSizeRegionBeforeMorph();
    this.#preserveSizebaySyncBeforeMorph();

    // Keep Sizebay's injected DOM across the morph: detach its #szb-look-N mounts
    // (which hold the trigger's #szb-chart-button) and freeze the size-guide
    // container (its adopted <span>). Sizebay will not re-render for the same
    // product, so we preserve rather than rebuild. Combined-listing switches run
    // through updateMain(), not here, so colourway re-rendering is unaffected.
    this.#detachSizebayLooksBeforeMorph();
    const frozenSizebayGuides = this.#freezeSizebayGuideBeforeMorph(newVariantPickerSource);

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });

    this.#unfreezeSizebayGuideAfterMorph(frozenSizebayGuides);
    this.#reattachSizebayLooksAfterMorph();

    // Size picks are re-applied from the click-time option_values after morph.
    this.#restoreSizeRegion();
    this.#restoreSizebaySyncAfterMorph();
    this.#refreshRadioCaches();
    requestAnimationFrame(() => {
      this.#restoreSizeRegion();
      this.#restoreSizebaySyncAfterMorph();
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Selected option value ids from the DOM (Horizon).
   * @returns {string[]}
   */
  get selectedOptionsValues() {
    /** @type {HTMLElement[]} */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
